import type { AdPlacement } from '../../advertisement/types/ad-slot-types'
import type { ContentBlocksAndAdSlotMarkers, DeviceData } from '../types/content-block-types'
import type { NewContentBlock } from '../stores/content-block-store'

/**
 * Represents a related articles block
 * This is injected between regular content blocks to indicate where related articles should be placed
 */
export type RelatedArticlesBlock = {
  paragraphType: '__injected_related__',
  relatedArticlesMarker: {
    injectedIndex?: number,
    injectedTotal?: number
  }
}

/**
 * Injects related articles blocks at appropriate positions in the content blocks array
 *
 * This function analyzes the content blocks and inserts related articles blocks based on the provided configuration.
 * It respects rules like minimum distance from the top, no related articles before/after specific block types,
 * and placement of different types of related articles blocks.
 *
 * @param blocks - Array of content blocks to process
 * @param placementConfig - Configuration for related articles placement rules
 * @param deviceData - Data about the device that is rendering the content, optional for server-side rendering
 * @returns Array of content blocks with related articles blocks injected at appropriate positions
 */
export function injectRelatedArticlesBlock (
  blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[],
  placementConfig?: AdPlacement | null,
  deviceData?: DeviceData
): NewContentBlock<ContentBlocksAndAdSlotMarkers>[] {
  if (blocks.length === 0) return []

  const effectiveConfig: AdPlacement = placementConfig || {
    firstDynamicAdAfterBlockNumber: Infinity,
    rectangleVsBannerSplit: 100,
    minimumDynamicAdDistance: 0,
    noDisplayAdBefore: [],
    noDisplayAdAfter: [],
    showOutstreamAd: false,
    noOutstreamAdBefore: [],
    noOutstreamAdAfter: [],
    placeOutstreamAfterAmountOfDisplayAds: undefined,
    firstRelatedBlockDistance: 0,
    minimumDistanceBetweenRelatedBlocks: 0,
    noRelatedBlockBefore: [],
    noRelatedBlockAfter: [],
    nativeTeasersPerRelatedBlock: 3,
    nativeTeasersPerQuadrupleTeaser: 4
  } as AdPlacement

  // For SSR, or when disabled, do not inject markers.
  if (!deviceData) return blocks
  if (!effectiveConfig.minimumDistanceBetweenRelatedBlocks || !effectiveConfig.firstRelatedBlockDistance) return blocks

  const noRelatedBlockAfter = effectiveConfig.noRelatedBlockAfter || []
  const noRelatedBlockBefore = effectiveConfig.noRelatedBlockBefore || []

  // Compute required gap in pixels from the top of the page; for example, if viewport is 800 and value is 200 then gap = 800 * 2 = 1600px.
  const firstGap = deviceData.viewportHeight * (effectiveConfig.firstRelatedBlockDistance / 100)
  const gap = deviceData.viewportHeight * (effectiveConfig.minimumDistanceBetweenRelatedBlocks / 100)

  // Initialize cumulative accumulator with the top offset from the first block
  const firstEnabledBlock = blocks.findIndex(block => block.enabled)
  let cumulative = blocks[firstEnabledBlock]!.dimensions.fromTop
  let useFirstGap = true
  const finalResult: NewContentBlock<ContentBlocksAndAdSlotMarkers>[] = []

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!
    finalResult.push(block)
    cumulative += block.dimensions.height

    // Candidate injection point: after current block i.
    let allowed = true
    // Check the current (previous) block against noRelatedBlockAfter
    let prevCheckBlock = block
    if (block.type === '__injected_ad__') {
      for (let k = i - 1; k >= 0; k--) {
        if (blocks[k]!.enabled && blocks[k]!.type !== '__injected_ad__') {
          prevCheckBlock = blocks[k]!
          break
        }
      }
    }
    if (prevCheckBlock.enabled && noRelatedBlockAfter.length > 0 && noRelatedBlockAfter.includes(prevCheckBlock.type)) {
      allowed = false
    }

    // Check the upcoming block against noRelatedBlockBefore if it exists
    let nextCheckBlock = null
    for (let j = i + 1; j < blocks.length; j++) {
      if (blocks[j]!.enabled && blocks[j]!.type !== '__injected_ad__') {
        nextCheckBlock = blocks[j]!
        break
      }
    }
    if (nextCheckBlock && noRelatedBlockBefore.length > 0 && noRelatedBlockBefore.includes(nextCheckBlock.type)) {
      allowed = false
    }

    if (useFirstGap) {
      if (cumulative < firstGap) {
        allowed = false
      } else {
        useFirstGap = false
      }
    } else {
      if (cumulative < gap) {
        allowed = false
      }
    }

    if (allowed) {
      const markerId = `injected-related-${i}`
      const markerBlock = {
        id: markerId,
        type: '__injected_related__',
        enabled: true,
        data: {
          relatedArticlesMarker: { id: markerId, enabled: ['desktop', 'mobile'], injectedIndex: 0, injectedTotal: 0 },
          paragraphType: '__injected_related__'
        } as RelatedArticlesBlock,
        dimensions: { width: 0, height: 0, fromTop: 0 }
      } as NewContentBlock<ContentBlocksAndAdSlotMarkers>
      finalResult.push(markerBlock)
      // Reset cumulative to negative of the current block's height to delay the next injection
      cumulative = -block.dimensions.height
    }
  }

  // Annotate each injected marker with metadata.
  const markers = finalResult.filter(block => block.type === '__injected_related__')
  const totalMarkers = markers.length
  let markerIndex = 0
  for (let i = 0; i < finalResult.length; i++) {
    if (finalResult[i]!.type === '__injected_related__') {
      const markerData = finalResult[i]!.data as RelatedArticlesBlock
      markerData.relatedArticlesMarker.injectedIndex = markerIndex
      markerData.relatedArticlesMarker.injectedTotal = totalMarkers
      markerIndex++
    }
  }

  return finalResult
}
